import { useState, useRef, useEffect, type ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import type { Mode, Period } from "../router/search";

interface DateNavProps {
  period: Period;
  from?: string;
  to?: string;
  mode?: Mode;
  fyStart?: string;
  /** The window the SERVER resolved (`meta.period`), as inclusive local dates.
   *  The navigator must show THIS, not the one it would work out from the
   *  browser clock: a multi-day window never includes the day in progress, and
   *  it ends at the latest day the engine has actually computed — so the two
   *  disagree, and the control was naming a window the screen was not showing. */
  resolved?: { from?: string; to?: string };
  onNavigate: (from: string, to: string) => void;
}

export function DateNav({ period, from, to, mode, fyStart = "04-01", resolved,
                         onNavigate }: DateNavProps): ReactNode {
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!pickerOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setPickerOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [pickerOpen]);

  // What this period WOULD span, and what the server actually served for it.
  const requested = resolveRange(period, from, to, fyStart);
  const range = resolved?.from && resolved?.to
    ? { from: resolved.from, to: resolved.to }
    : requested;
  const label = formatLabel(period, range.from, range.to);
  // The server returning a shorter window than was asked for IS the signal that
  // there is nothing further forward to show — it clamps to the last completed,
  // computed day. Reading it off the response means the browser does not have to
  // know where the engine has got to.
  const atLatest = Boolean(resolved?.to && requested.to > resolved.to);
  const canForward =
    mode === "forecast" || (!rangeIncludesToday(range.to) && !atLatest);

  function step(direction: -1 | 1): void {
    const [newFrom, newTo] = stepRange(period, range.from, range.to, direction, fyStart);
    onNavigate(newFrom, newTo);
  }

  return (
    <div className="datenav" style={{ position: "relative" }}>
      <span aria-label="Previous period" role="button" tabIndex={0} onClick={() => step(-1)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") step(-1); }}>
        <ChevronLeft size={14} />
      </span>

      <b role="button" tabIndex={0} aria-label="Open date picker"
        onClick={() => setPickerOpen((v) => !v)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setPickerOpen((v) => !v); }}>
        {label}
      </b>

      <span
        aria-label="Next period"
        role="button"
        tabIndex={canForward ? 0 : -1}
        aria-disabled={!canForward}
        style={canForward ? undefined : disabledArrow}
        onClick={() => { if (canForward) step(1); }}
        onKeyDown={(e) => { if (canForward && (e.key === "Enter" || e.key === " ")) step(1); }}
      >
        <ChevronRight size={14} />
      </span>

      {pickerOpen ? (
        <>
          <button
            type="button"
            aria-label="Close"
            style={{ position: "fixed", inset: 0, background: "transparent", border: 0, zIndex: 79 }}
            onClick={() => setPickerOpen(false)}
          />
          <div ref={pickerRef} style={pickerStyle}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: 12 }}>
              <label style={pickerLabelStyle}>
                From
                <input type="date" value={range.from} max={range.to}
                  style={pickerInput} aria-label="From date"
                  onChange={(e) => { onNavigate(e.target.value, range.to); }} />
              </label>
              <label style={pickerLabelStyle}>
                To
                <input type="date" value={range.to} min={range.from}
                  style={pickerInput} aria-label="To date"
                  onChange={(e) => { onNavigate(range.from, e.target.value); }} />
              </label>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

/* ── Date helpers ─────────────────────────────────────────────────── */

/* ── Local-date arithmetic ─────────────────────────────────────────
   Every date here is a CALENDAR day, never an instant, so none of it may go
   through `toISOString()`: that converts to UTC first. East of Greenwich the
   round trip lands on the previous day, which is why the navigator read
   "9 Oct" on the 10th and why one click of the back arrow moved TWO days
   (local midnight -> minus a day -> back to UTC -> minus another). */

/** A Date's own calendar day as YYYY-MM-DD, read in the browser's zone. */
function isoOf(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

function isoToday(): string {
  return isoOf(new Date());
}

function rangeIncludesToday(to: string): boolean {
  return to >= isoToday();
}

export function resolveRange(
  period: Period,
  from: string | undefined,
  to: string | undefined,
  fyStart: string,
): { from: string; to: string } {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth();
  const d = today.getDate();

  switch (period) {
    case "today":
      return { from: iso(y, m, d), to: iso(y, m, d) };
    case "7d":
      return { from: iso(y, m, d - 6), to: iso(y, m, d) };
    case "30d":
      return { from: iso(y, m, d - 29), to: iso(y, m, d) };
    case "mtd":
      return { from: iso(y, m, 1), to: iso(y, m, d) };
    case "qtd": {
      const qStart = m - (m % 3);
      return { from: iso(y, qStart, 1), to: iso(y, m, d) };
    }
    case "fy": {
      const [fmStr, fdStr] = fyStart.split("-");
      const fm = parseInt(fmStr!, 10) - 1;
      const fd = parseInt(fdStr!, 10);
      const fyYear = m >= fm ? y : y - 1;
      return { from: iso(fyYear, fm, fd), to: iso(y, m, d) };
    }
    case "ytd":
      return { from: iso(y, 0, 1), to: iso(y, m, d) };
    case "lifetime":
      return { from: "2000-01-01", to: iso(y, m, d) };
    case "custom":
      return { from: from ?? iso(y, m, d - 29), to: to ?? iso(y, m, d) };
    default:
      return { from: iso(y, m, d), to: iso(y, m, d) };
  }
}

export function stepRange(
  period: Period,
  from: string,
  to: string,
  direction: -1 | 1,
  fyStart: string,
): [string, string] {
  const fd = new Date(from + "T00:00:00");
  const td = new Date(to + "T00:00:00");

  switch (period) {
    case "today":
    case "7d":
    case "30d": {
      const span = Math.round((td.getTime() - fd.getTime()) / 86_400_000) + 1;
      const shift = span * direction;
      return [shiftDays(from, shift), shiftDays(to, shift)];
    }
    case "mtd": {
      const m = fd.getMonth() + direction;
      const y = fd.getFullYear();
      const newFrom = iso(y, m, 1);
      const newTo = isoEndOfMonth(y, m);
      return [newFrom, newTo];
    }
    case "qtd": {
      const qm = fd.getMonth() + 3 * direction;
      const y = fd.getFullYear();
      const newFrom = iso(y, qm, 1);
      const newTo = isoEndOfMonth(y, qm + 2);
      return [newFrom, newTo];
    }
    case "fy": {
      const [fmStr, fdStr] = fyStart.split("-");
      const fm = parseInt(fmStr!, 10) - 1;
      const fDay = parseInt(fdStr!, 10);
      const curFyYear = fd.getFullYear();
      const newFyYear = curFyYear + direction;
      const newFrom = iso(newFyYear, fm, fDay);
      const endDate = new Date(newFrom + "T00:00:00");
      endDate.setFullYear(endDate.getFullYear() + 1);
      endDate.setDate(endDate.getDate() - 1);
      const newTo = isoOf(endDate);
      return [newFrom, newTo];
    }
    case "ytd": {
      const y = fd.getFullYear() + direction;
      return [iso(y, 0, 1), iso(y, 11, 31)];
    }
    case "custom": {
      const span = Math.round((td.getTime() - fd.getTime()) / 86_400_000) + 1;
      const shift = span * direction;
      return [shiftDays(from, shift), shiftDays(to, shift)];
    }
    default:
      return [from, to];
  }
}

function formatLabel(period: Period, from: string, to: string): string {
  const fd = new Date(from + "T00:00:00");
  const td = new Date(to + "T00:00:00");

  switch (period) {
    case "today":
      return fmtDate(fd);
    case "mtd":
      return fmtMonthYear(fd);
    case "qtd": {
      const q = Math.floor(fd.getMonth() / 3) + 1;
      return `Q${q} ${fd.getFullYear()}`;
    }
    case "fy": {
      const startY = fd.getFullYear();
      const endY = startY + 1;
      return `FY ${startY}–${String(endY).slice(2)}`;
    }
    case "ytd":
      return `${fd.getFullYear()} to date`;
    case "7d":
    case "30d":
    case "custom":
      return `${fmtShort(fd)} – ${fmtShort(td)}`;
    case "lifetime":
      return "All time";
    default:
      return `${fmtShort(fd)} – ${fmtShort(td)}`;
  }
}

/* ── Tiny date utils ──────────────────────────────────────────────── */

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function iso(y: number, m: number, d: number): string {
  return isoOf(new Date(y, m, d));
}

function isoEndOfMonth(y: number, m: number): string {
  return isoOf(new Date(y, m + 1, 0));
}

function shiftDays(isoDate: string, days: number): string {
  const d = new Date(isoDate + "T00:00:00");
  d.setDate(d.getDate() + days);
  return isoOf(d);
}

function fmtDate(d: Date): string {
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
}

function fmtShort(d: Date): string {
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
}

function fmtMonthYear(d: Date): string {
  return `${MONTHS_LONG[d.getMonth()]} ${d.getFullYear()}`;
}

/* ── Styles ───────────────────────────────────────────────────────── */

const disabledArrow: React.CSSProperties = {
  opacity: 0.35,
  cursor: "not-allowed",
  pointerEvents: "none",
};

const pickerStyle: React.CSSProperties = {
  position: "absolute",
  top: "calc(100% + 4px)",
  left: 0,
  zIndex: 80,
  background: "var(--sk-color-surface-0)",
  border: "1px solid var(--sk-color-border-1)",
  borderRadius: 8,
  boxShadow: "0 4px 16px rgba(10,10,15,.12)",
};

const pickerLabelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  fontSize: 11,
  fontWeight: 600,
  color: "var(--sk-color-ink-3)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const pickerInput: React.CSSProperties = {
  height: 28,
  border: "1px solid var(--sk-color-border-2)",
  borderRadius: "var(--sk-radius-control, 6px)",
  background: "var(--sk-color-surface-2)",
  color: "var(--sk-color-ink-1)",
  fontSize: 12,
  padding: "0 8px",
};
